import { useEffect, useRef, useState } from "react";

/**
 * Background film for a full-bleed section.
 *
 * Exists because the landing hero previously shipped *two* <video> elements —
 * one `hidden md:block`, one `md:hidden` — each with `preload="auto"`. CSS
 * `display:none` does not stop a video element from fetching, so every visitor
 * downloaded both cuts (≈1.8 MB) and the creative changed at the md breakpoint.
 * One element, one source, cropped with object-fit, fixes both.
 *
 * Behaviour worth knowing:
 *
 * - `prefers-reduced-motion: reduce` gets the poster as a still image and the
 *   video is never requested at all. The preference is read once per mount and
 *   then watched, so it does not re-render on resize.
 * - `defer` holds the `src` back until the element is near the viewport. The
 *   bottom-of-page CTA reprises the hero film; without this both elements
 *   raced for the same URL on load and the browser fetched it twice in
 *   parallel rather than reusing the response.
 * - The element never changes identity across breakpoints, so resizing cannot
 *   remount the player or restart playback.
 * - If the video errors (offline, unsupported codec) the poster stays on
 *   screen: `object-fit` and dimensions live on the element, not on the media.
 */
export default function HeroVideo({
  src,
  srcMobile,
  poster,
  className = "",
  objectPosition,
  defer = false,
}: {
  src: string;
  /**
   * Optional narrow-viewport cut. A phone pulling the desktop master is the
   * single most expensive thing on a mobile first view (the hero master is
   * ~1.8MB against ~170KB for the 720px cut), and it is displayed in a box a
   * third of the width. Chosen with matchMedia rather than <source media>
   * because the element sets `src` directly and the two do not mix.
   */
  srcMobile?: string;
  poster: string;
  className?: string;
  objectPosition?: string;
  /** Wait until the section is near the viewport before fetching the video. */
  defer?: boolean;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(max-width: 700px)").matches === true
  );
  useEffect(() => {
    if (!srcMobile) return;
    const mq = window.matchMedia("(max-width: 700px)");
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [srcMobile]);
  const chosen = srcMobile && narrow ? srcMobile : src;
  const [reducedMotion, setReducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
  // When deferring we start with no source and let the observer supply it.
  const [active, setActive] = useState(!defer);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!defer || active) return;
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setActive(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setActive(true);
          io.disconnect();
        }
      },
      // Start fetching a little before it scrolls in, so the poster is not
      // still sitting there when the section arrives.
      { rootMargin: "400px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [defer, active]);

  // Autoplay can be refused (low-power mode, iOS data saver). Ask once and
  // swallow the rejection — the poster is a perfectly good fallback and the
  // page must not depend on playback.
  useEffect(() => {
    if (reducedMotion || !active) return;
    const el = ref.current;
    if (!el) return;
    const play = el.play();
    if (play && typeof play.catch === "function") play.catch(() => {});
  }, [reducedMotion, active]);

  const style = objectPosition ? { objectPosition } : undefined;

  if (reducedMotion) {
    return (
      <img
        src={poster}
        alt=""
        aria-hidden
        className={className}
        style={style}
        decoding="async"
        fetchPriority={defer ? "low" : "high"}
      />
    );
  }

  return (
    <video
      ref={ref}
      className={className}
      style={style}
      poster={poster}
      src={active ? chosen : undefined}
      autoPlay
      muted
      loop
      playsInline
      // `metadata` is enough to start a looping muted background clip; `auto`
      // told the browser to pull the whole file at document priority.
      preload={active ? "metadata" : "none"}
      aria-hidden
      tabIndex={-1}
    />
  );
}
